import {
  useEffect,
  useId,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AnimatePresence,
  useAnimationControls,
  useDragControls,
} from "framer-motion";

import { isBrowser } from "~/utils/helpers";
import { Duration } from "~/constants";

import { SheetModalBackdrop } from "./SheetModalBackdrop";
import { SheetModalBodyContainer } from "./SheetModalBodyContainer";
import { SheetModalCoordinator } from "./SheetModalCoordinator";
import { SheetModalHeader } from "./SheetModalHeader";
import { SheetModalPanel } from "./SheetModalPanel";
import { SheetModalBody } from "./SheetModalBody";

import "./SheetModal.css";

import type { ReactNode, CSSProperties } from "react";
import type {
  AnimationControls,
  DragControls,
  Easing,
  Transition,
} from "framer-motion";
import type { SizeProp } from "../components";

export interface SheetModalProps {
  children?: ReactNode;
  disableBackdrop?: boolean;
  disableGestures?: boolean;
  disableGrabber?: boolean;
  disableOutsideDismiss?: boolean;
  header?: ReactNode | ((ctx: SheetModalContextValue) => ReactNode);
  isOpen?: boolean;
  root?: HTMLElement | null;
  scrollable?: boolean;
  size?: Extract<SizeProp, "lg">;
  style?: CSSProperties;
  onRequestClose?: () => void;
  onUnmount?: () => void;
}

export interface SheetModalContextValue
  extends Pick<
    SheetModalProps,
    | "disableBackdrop"
    | "disableGestures"
    | "disableGrabber"
    | "disableOutsideDismiss"
    | "scrollable"
    | "size"
  > {
  animationControls?: AnimationControls;
  backdropControls?: AnimationControls;
  dragControls?: DragControls;
  requestClose?: () => void;
}

export const SHEET_MODAL_DURATION = Duration.Normal / 1_000;
export const SHEET_MODAL_EASE: Easing = [0.4, 0, 0.2, 1];
export const SHEET_MODAL_TRANSITION: Transition = {
  type: "tween",
  duration: SHEET_MODAL_DURATION,
  ease: SHEET_MODAL_EASE,
};

export const SheetModalContext = createContext<SheetModalContextValue>({});

export function useSheetModal() {
  return useContext(SheetModalContext);
}

export function SheetModal({
  children,
  disableGestures,
  header,
  disableBackdrop,
  disableGrabber,
  disableOutsideDismiss,
  isOpen,
  root,
  scrollable,
  size,
  style,
  onRequestClose,
  onUnmount,
}: SheetModalProps) {
  const id = useId();

  const dragControls = useDragControls();
  const animationControls = useAnimationControls();
  const backdropControls = useAnimationControls();

  const [mounted, setMounted] = useState(isOpen);

  const _style: CSSProperties = {
    "--sheet-modal-strategy": root ? "absolute" : undefined,
    ...style,
  };

  const requestClose = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  const ctx = useMemo<SheetModalContextValue>(
    () => ({
      animationControls,
      disableBackdrop,
      disableGestures,
      disableGrabber,
      disableOutsideDismiss,
      dragControls,
      backdropControls,
      scrollable,
      size,
      requestClose,
    }),
    [
      animationControls,
      backdropControls,
      disableBackdrop,
      disableGestures,
      disableGrabber,
      disableOutsideDismiss,
      dragControls,
      requestClose,
      scrollable,
      size,
    ],
  );

  const handleExitComplete = useCallback(() => {
    setMounted(false);
    onUnmount?.();
  }, [onUnmount]);

  const renderHeader = useCallback(() => {
    if (header === undefined) return <SheetModalHeader />;

    if (typeof header === "function") {
      return header(ctx);
    }

    return header;
  }, [ctx, header]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    }

    SheetModalCoordinator.shared.handlePropsChange({
      id,
      isOpen: Boolean(isOpen),
    });

    return () => {
      SheetModalCoordinator.shared.handlePropsChange({ id, isOpen: false });
    };
  }, [id, isOpen]);

  // Only render in the browser (portals are browser only).
  // Don't render when closed and animations are complete.
  if (!isBrowser() || !mounted) return null;

  return createPortal(
    <SheetModalContext.Provider value={ctx}>
      <div className="SheetModal" aria-live="polite" style={_style}>
        <AnimatePresence onExitComplete={handleExitComplete}>
          {isOpen && (
            <>
              {!disableBackdrop && <SheetModalBackdrop key="backdrop" />}

              <SheetModalPanel key="panel">
                {renderHeader()}

                <SheetModalBodyContainer>{children}</SheetModalBodyContainer>
              </SheetModalPanel>
            </>
          )}
        </AnimatePresence>
      </div>
    </SheetModalContext.Provider>,
    root ?? globalThis.document.body,
  );
}

SheetModal.Body = SheetModalBody;
